import trackingEvents from "./trackingEvents";
import { getSingleJob } from "./SegmentWrapper";

const DEBUG = process.env.NODE_ENV !== "production";
let currentTrackingContext = {};

// Namespace our global variables to avoid conflicts with OneTrust
const TRACKING_NAMESPACE = "PGA_JOBS_TRACKING";

// Helper function to safely access/set namespaced globals
const getNamespacedGlobal = (key, defaultValue = {}) => {
  if (typeof window === "undefined") return defaultValue;

  if (!window[TRACKING_NAMESPACE]) {
    window[TRACKING_NAMESPACE] = {};
  }

  if (window[TRACKING_NAMESPACE][key] === undefined) {
    window[TRACKING_NAMESPACE][key] = defaultValue;
  }

  return window[TRACKING_NAMESPACE][key];
};

const setNamespacedGlobal = (key, value) => {
  if (typeof window === "undefined") return;

  if (!window[TRACKING_NAMESPACE]) {
    window[TRACKING_NAMESPACE] = {};
  }

  window[TRACKING_NAMESPACE][key] = value;
};

/* OneTrust readiness check */
const isOneTrustReady = () => {
  try {
    // Check if OneTrust is loaded and initialized
    return (
      typeof window !== "undefined" &&
      window.OneTrust &&
      typeof window.OneTrust.IsAlertBoxClosed === "function" &&
      window.Optanon &&
      typeof window.Optanon.GetDomainData === "function"
    );
  } catch (error) {
    log("OneTrust readiness check failed", error, "warn");
    return false;
  }
};

/* Wait for consent systems to be ready */
const waitForConsentSystems = () => {
  return new Promise((resolve) => {
    // Check immediately
    if (isOneTrustReady()) {
      log("OneTrust is ready, proceeding with tracking initialization");
      resolve(true);
      return;
    }

    // Check periodically
    const interval = setInterval(() => {
      if (isOneTrustReady()) {
        clearInterval(interval);
        log("OneTrust is ready, proceeding with tracking initialization");
        resolve(true);
      }
    }, 100);

    // Cleanup interval after 10 seconds
    setTimeout(() => {
      clearInterval(interval);
      log(
        "OneTrust readiness timeout, proceeding with tracking initialization",
        {},
        "warn",
      );
      resolve(false);
    }, 10000);
  });
};

/* Enhanced logging */
const log = (message, data = {}, type = "log") => {
  if (DEBUG) {
    console[type](`[Tracking] ${message}`, data);
  }
};

export const initTrackingContext = (context = {}) => {
  currentTrackingContext = {
    ...currentTrackingContext,
    ...context,
  };
};

/* Active section detection */
const getActiveSection = () => {
  try {
    const activeSelectors = [
      "header a.active",
      ".nav-link.active",
      '[aria-current="page"]',
      '[data-active="true"]',
    ];

    const activeElement = document.querySelector(activeSelectors.join(", "));
    if (activeElement) {
      return activeElement.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    }

    const { pathname } = window.location;
    return pathname.split("/")[1] || "home";
  } catch (error) {
    log("Section detection failed", error, "error");
    return "unknown";
  }
};

/* Element data extraction */
const getElementData = (element) => {
  const data = {
    element,
    text: element.textContent,
    href: element.href || null,
    page_url: window.location.href,
    page_path: getActiveSection(),
    element_type: element.tagName.toLowerCase(),
    classes: element.className || "",
    attributes: {},
    dataset: {},
  };

  Array.from(element.attributes).forEach((attr) => {
    if (attr.name.startsWith("data-")) {
      const key = attr.name.replace("data-", "");
      data.attributes[key] = attr.value;
      data.dataset[key] = attr.value;
    }
  });

  return data;
};

/* Improved Job Tile detection */
const isJobTileLink = (elementData) => {
  try {
    if (!elementData.href) return false;

    const url = new URL(elementData.href);
    return (
      url.hostname.includes("jobs") &&
      url.hostname.includes("pga") &&
      url.pathname.includes("/detail/")
    );
  } catch {
    return false;
  }
};

const isExternalApplication = (elementData) => {
  try {
    if (elementData.text) {
      return (
        elementData.text.toLowerCase().includes("apply") &&
        elementData.href !== null
      );
    }
  } catch {
    return false;
  }
};

/* Event matching logic */
const matchEventForElement = (element, elementData) => {
  // 1. Check explicit track-event attribute
  if (elementData.attributes["track-event"]) {
    const matchedEvent = trackingEvents.events.find(
      (e) => e.eventName === elementData.attributes["track-event"],
    );
    if (matchedEvent) return matchedEvent;
  }
  // 2. Special cases
  if (isSavePreferencesButton(elementData))
    return getEvent("Save Job Preferences");
  if (isJobTileLink(elementData)) return getEvent("Click Job Tile");
  if (isSearchForJobsButton(elementData))
    return getEvent("Click Search for Jobs");
  if (isSubmitJobApplication(elementData)) {
    if (validateApplicationData()) {
      return getEvent("Submit Job Application");
    }
  }
  if (isExternalApplication(elementData))
    return getEvent("Click Apply External Link");
  if (isClickViewAllRecommendedJobs(elementData))
    return getEvent("Click View All Recommended Jobs");

  // 3. Known patterns
  for (const event of trackingEvents.events) {
    if (matchesEventPattern(element, elementData, event)) {
      return event;
    }
  }
  // 4. Default for clickable elements
  if (isClickableElement(element)) return getEvent("Click Link");

  return null;
};

const validateApplicationData = () => {
  if (
    document.getElementsByName("firstName")[0].value !== "" &&
    document.getElementsByName("lastName")[0].value !== "" &&
    document.getElementsByName("emailAddress")[0].value !==
      "" /*&& document.getElementsByName('resume')[0].files.length > 0*/
  ) {
    return true;
  } else {
    return false;
  }
};

/* Helper functions */
const getEvent = (eventName) => {
  return (
    trackingEvents.events.find((e) => e.eventName === eventName) || {
      eventName,
      properties: [
        { name: "page_url" },
        { name: "page_path" },
        { name: "text" },
        { name: "href" },
        { name: "element_type" },
      ],
    }
  );
};

const isSavePreferencesButton = (elementData) => {
  return (
    elementData.element_type === "button" &&
    (elementData.text.includes("save preferences") ||
      elementData.text.includes("guardar preferencias"))
  );
};

const isSearchForJobsButton = (elementData) => {
  return (
    elementData.element_type === "button" &&
    elementData.text.includes("Search") &&
    !elementData.text.includes("Advanced")
  );
};

const isSubmitJobApplication = (elementData) => {
  return (
    elementData.element_type === "button" &&
    elementData.text.includes("Submit Application")
  );
};

const isClickViewAllRecommendedJobs = (elementData) => {
  return (
    elementData.element_type === "button" &&
    elementData.text.includes("View All Recommended Jobs")
  );
};

const isClickableElement = (element) => {
  return element.matches('a, button, [role="button"]');
};

/* Pattern matching */
const matchesEventPattern = (element, elementData, event) => {
  const { text, classes } = elementData;

  switch (event.eventName) {
    case "Click Nav Menu Item":
      return element.closest("nav, header") !== null;

    case "Click Log in":
      return text.includes("log in") || text.includes("sign in");

    case "Click Get Started":
      return text.includes("get started");

    case "Click News Tile":
      return classes.includes("news-tile");

    default:
      return false;
  }
};

/* Property collection */
const collectEventProperties = (event, elementData, rawEvent) => {
  const props = {
    page_url: elementData.page_url,
    page_path: elementData.page_path,
    ...currentTrackingContext,
  };

  if (event.eventName === "Click Link") {
    try {
      props.destination_url = elementData.href;
      props.link_text = elementData.text;
    } catch (error) {
      log("Failed to extract link props", error, "error");
    }
  }

  if (event.eventName === "Click Nav Menu Item") {
    try {
      props.destination_url = elementData.href;
      props.link_text = elementData.text;
      props.menu_type = "Main Nav";
      props.nav_item = elementData.text;
    } catch (error) {
      log("Failed to extract nav menu props", error, "error");
    }
  }

  if (event.eventName === "Click Search for Jobs") {
    try {
      const keywords = document.querySelector(
        '[placeholder="Job Title, Keywords"',
      );
      props.job_keyword = keywords ? keywords.value : "";
    } catch (error) {
      log("Failed to extract job Keywords", error, "error");
    }
  }

  if (
    event.eventName === "Submit Job Application" ||
    event.eventName === "Click Apply External Link" ||
    (event.eventName === "Click Job Tile" && elementData.href)
  ) {
    try {
      let jobId = "";
      if (event.eventName === "Click Job Tile") {
        const url = new URL(elementData.href);
        jobId = url.pathname.split("/").pop();
      } else {
        const match = rawEvent.srcElement.baseURI.match(/\/detail\/(.*)$/);
        jobId = match ? match[1] : "";
      }

      getSingleJob(jobId)
        .then((singleJob) => {
          // Check if we have results and get the first job
          if (
            !singleJob ||
            !Array.isArray(singleJob) ||
            singleJob.length === 0
          ) {
            console.warn("No job found for ID:", jobId);
            return;
          }

          singleJob = singleJob[0];

          // Add additional safety check for the job object
          if (!singleJob) {
            console.warn("Invalid job data for ID:", jobId);
            return;
          }

          let pga_pro_only;
          let max_salary = 0;
          let hourly_rate_range = 0;

          if (singleJob.applicant_group === "PGA Professional Only") {
            pga_pro_only = true;
          } else {
            pga_pro_only = false;
          }

          if (singleJob.pay_schedule === "Salary") {
            if (
              singleJob.base_compensation_max &&
              singleJob.base_compensation_max.value
            ) {
              max_salary = singleJob.base_compensation_max.value;
            } else if (
              singleJob.base_compensation &&
              singleJob.base_compensation.value
            ) {
              max_salary = singleJob.base_compensation.value;
            }
          } else {
            if (
              singleJob.base_compensation &&
              singleJob.base_compensation.value
            ) {
              hourly_rate_range = singleJob.base_compensation.value;
            }
          }

          // Safely extract location info
          const location =
            singleJob.facilities && singleJob.facilities[0]
              ? `${singleJob.facilities[0].city || ""}, ${
                  singleJob.facilities[0].state || ""
                }`
              : "";

          // Safely extract company info
          const company =
            singleJob.offices && singleJob.offices[0]
              ? singleJob.offices[0].name || ""
              : "";

          // Safely extract education info
          const education =
            singleJob.education && singleJob.education[0]
              ? singleJob.education[0]
              : false;

          window.analytics.track(event.eventName, {
            job_id: singleJob.jobId || "",
            job_location: location,
            job_post_date: singleJob.created
              ? new Date(singleJob.created * 1000).toISOString()
              : "",
            job_deadline: singleJob.submission_deadline
              ? new Date(singleJob.submission_deadline * 1000).toISOString()
              : "",
            job_position_type: singleJob.employment_type || "",
            job_minimum_experience: singleJob.years_experience || "",
            job_company: company,
            job_title: singleJob.jobTitle || "",
            job_category: singleJob.job_category || "",
            job_additional_pga_education:
              singleJob.additional_pga_education || "",
            job_education_required: education,
            job_pga_pro_only: pga_pro_only,
            job_max_salary: max_salary || 0,
            job_hourly_rate_range: hourly_rate_range || 0,
            page_url: window.location.href,
            page_path: window.location.pathname,
          });
        })
        .catch((error) => {
          console.error("Error tracking job event:", error);
        });
      props.dont_send = true;
    } catch (error) {
      console.error("Error try catch event:", error);
    }
  }

  // Map other properties
  event.properties.forEach((prop) => {
    const value =
      elementData[prop.name] ||
      elementData.attributes[prop.name] ||
      elementData.dataset[prop.name];
    if (value !== undefined) {
      props[prop.name] = value;
    }
  });

  return props;
};

function trackRolexWidgetClick() {
  const iframe = document.querySelector("iframe");
  if (!iframe || !window.analytics) return;

  const parent = iframe.parentElement;

  if (window.getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }

  const overlay = document.createElement("div");
  Object.assign(overlay.style, {
    position: "absolute",
    top: `${iframe.offsetTop}px`,
    left: `${iframe.offsetLeft}px`,
    width: `${iframe.offsetWidth}px`,
    height: `${iframe.offsetHeight}px`,
    backgroundColor: "transparent",
    zIndex: "9999",
    cursor: "pointer",
    pointerEvents: "auto",
  });

  overlay.addEventListener("click", () => {
    window.analytics.track("Click Rolex Icon", {
      page_url: window.location.href,
      page_path: getActiveSection(),
    });

    window.open(
      "https://www.rolex.com/?cmpid=dsp_Clock_pga.org/_2021_rlx2101379",
      "_blank",
    );
  });

  parent.appendChild(overlay);
}

/* Click handler */
const handleTrackClick = (event) => {
  // Safety checks to avoid interfering with consent systems
  if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

  // Don't track clicks on OneTrust elements
  if (
    event.target.closest(
      "#onetrust-banner-sdk, #onetrust-pc-sdk, .ot-sdk-container",
    )
  ) {
    return;
  }

  const targetElement = event.target.closest('a, button, [role="button"]');
  if (!targetElement) return;

  if (targetElement.getAttribute("href") === "/logout") {
    if (sessionStorage.getItem("segment_userId") && window.analytics) {
      sessionStorage.setItem("segment_userId", "anonymous");
      localStorage.setItem("last_identified_traits", JSON.stringify("{}"));
      window.analytics.reset();
      return;
    }
  }

  try {
    const elementData = getElementData(targetElement);
    const matchedEvent = matchEventForElement(targetElement, elementData);

    if (!matchedEvent) {
      return;
    }

    const props = collectEventProperties(matchedEvent, elementData, event);
    const properties = JSON.parse(JSON.stringify(props));
    delete properties.jobsData;
    delete properties.allJobs;
    delete properties.isLoggedIn;
    delete properties.user;
    if (window.analytics && window.analytics.track && !properties.dont_send) {
      window.analytics.track(matchedEvent.eventName, properties);
    }
  } catch (error) {
    log("Tracking error", error, "error");
  }
};

function toSnakeCase(text) {
  return text.trim().toLowerCase().replace(/\s+/g, "_");
}

const getSearchResultsData = (props) => {
  const searchInput = document.querySelector(
    '[placeholder="Job Title, Keywords"]',
  );
  if (!searchInput) {
    log(
      "Search input not found when tracking search results",
      {
        page_url: window.location.href,
        timing: "getSearchResultsData",
      },
      "warn",
    );
  }
  props.job_search_keyword =
    searchInput && searchInput.value ? searchInput.value : "";
  const hitsList = document.querySelector(".ais-Hits-list");
  props.job_search_results_count = hitsList ? hitsList.childElementCount : 0;
  if (document.querySelector("#location-zipcode")) {
    if (document.querySelector("#location-zipcode").value === "")
      delete props["job_search_refinements"].search_city;
    else
      props["job_search_refinements"].search_city =
        document.querySelector("#location-zipcode").value;
  }

  const radiusInput = document.querySelector(".form-inline select");
  if (radiusInput) {
    props["job_search_refinements"].radius = radiusInput.value;
  }
  window.analytics.track("View Job Search Results", props);
};

function getRefinements(event) {
  if (event.target.tagName === "INPUT") return;
  const refinements = getNamespacedGlobal("jobSearchRefinements", {});

  const label = event.target.closest(".ais-RefinementList-label");
  if (!label) return;

  let wrapper = label.parentElement;
  while (wrapper && !wrapper.querySelector("h5")) {
    wrapper = wrapper.parentElement;
  }
  if (!wrapper) return;

  const h5Element = wrapper.querySelector("h5");
  const h5Text = h5Element ? toSnakeCase(h5Element.textContent.trim()) : "";
  if (!h5Text) return;

  const clickedText =
    event.target.textContent.trim() ||
    label.querySelector(".ais-RefinementList-labelText").textContent.trim() ||
    "";
  if (!clickedText) return;

  if (!Array.isArray(refinements[h5Text])) {
    refinements[h5Text] = [];
  }

  if (h5Text === "date_range") refinements[h5Text] = [];
  const list = refinements[h5Text];
  const index = list.indexOf(clickedText);

  if (index === -1) {
    list.push(clickedText);
  } else {
    list.splice(index, 1);
    if (list.length === 0) {
      delete refinements[h5Text];
    }
  }
  setNamespacedGlobal("jobSearchRefinements", refinements);
  syncFilters();
}

function syncFilters() {
  const refinements = getNamespacedGlobal("jobSearchRefinements", {});
  sessionStorage.setItem("jobSearchRefinements", JSON.stringify(refinements));
  const refinementsClone = JSON.parse(JSON.stringify(refinements));

  if (refinementsClone.length) {
    Object.keys(refinementsClone).forEach((key) => {
      const list = refinementsClone[key];
      const copy = [...list];

      copy.forEach((item) => {
        const itemExists = Array.from(
          document.querySelectorAll(".ais-RefinementList-labelText"),
        ).some((el) => el.textContent.trim() === item);

        if (!itemExists) {
          const index = list.indexOf(item);
          if (index > -1) {
            list.splice(index, 1);
          }
        }
      });

      if (list.length === 0) {
        delete refinementsClone[key];
      }
    });
  }

  setNamespacedGlobal("jobSearchRefinements", refinementsClone);
  const searchInput = document.querySelector(
    '[placeholder="Job Title, Keywords"]',
  );

  if (!searchInput) {
    log(
      "Search input not found when syncing filters",
      {
        page_url: window.location.href,
        timing: "syncFilters",
        refinements_count: Object.keys(refinementsClone).length,
      },
      "warn",
    );
  }

  getSearchResultsData({
    job_search_keyword:
      searchInput && searchInput.value ? searchInput.value : "",
    job_search_refinements: refinementsClone,
    job_search_results_count: "",
    page_path: window.location.pathname,
    page_url: window.location.href,
  });
}

const getSearchRefinements = () => {
  const filtersContainer = document.querySelector(
    'div[class*="ais-InstantSearch__root"] > div:nth-child(2) > div > div:first-child',
  );
  if (filtersContainer && !filtersContainer.dataset.listenerAttached) {
    filtersContainer.dataset.listenerAttached = "true";

    filtersContainer.addEventListener("click", function (event) {
      setTimeout(() => {
        getRefinements(event);
      }, 2000);
    });

    const radiusInput = document.querySelector(".form-inline select");
    if (radiusInput) {
      radiusInput.addEventListener("change", function () {
        setTimeout(() => {
          syncFilters();
        }, 2000);
      });
    }
  }
};

export const trackViewJobsSearch = () => {
  const storedRefinements = sessionStorage.getItem("jobSearchRefinements");
  if (storedRefinements) {
    try {
      setNamespacedGlobal(
        "jobSearchRefinements",
        JSON.parse(storedRefinements),
      );
    } catch (e) {
      setNamespacedGlobal("jobSearchRefinements", {});
    }
  } else {
    setNamespacedGlobal("jobSearchRefinements", {});
  }

  const initListener = (input) => {
    if (!input || input.dataset.listenerAttached === "true") return;

    input.dataset.listenerAttached = "true";
    getNamespacedGlobal("jobSearchRefinements", {});
    setNamespacedGlobal(
      "latestInputValue",
      getNamespacedGlobal("latestInputValue", ""),
    );
    setNamespacedGlobal(
      "debounceTimer",
      getNamespacedGlobal("debounceTimer", null),
    );

    if (window.location.href.includes("search")) {
      getSearchRefinements();
    }

    input.addEventListener("input", function (e) {
      setNamespacedGlobal("latestInputValue", e.target.value);
      clearTimeout(getNamespacedGlobal("debounceTimer", null));

      setNamespacedGlobal(
        "debounceTimer",
        setTimeout(() => {
          syncFilters();
        }, 2000),
      );
    });
  };

  const titleInput = document.querySelector(
    '[placeholder="Job Title, Keywords"]',
  );
  const cityInput = document.querySelector("#location-zipcode");

  if (titleInput) {
    initListener(titleInput);
  }

  if (cityInput) {
    initListener(cityInput);
  }

  const titleObserver = new MutationObserver(() => {
    if (titleInput) {
      initListener(titleInput);
      titleObserver.disconnect();
    }
  });

  const cityObserver = new MutationObserver(() => {
    if (cityInput) {
      initListener(cityInput);
      cityObserver.disconnect();
    }
  });

  titleObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  cityObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

/* Initialize tracking */
export const initTracking = () => {
  if (typeof window === "undefined") return;

  // Wait for consent systems to be ready before initializing tracking
  waitForConsentSystems()
    .then((isReady) => {
      try {
        if (isReady) {
          log("Initializing tracking after consent systems ready");
        } else {
          log(
            "Consent systems not ready within timeout, proceeding with caution",
            {},
            "warn",
          );
        }

        document.addEventListener("click", handleTrackClick, {
          capture: true,
          passive: true,
        });

        trackRolexWidgetClick();
      } catch (error) {
        log("Error during tracking initialization", error, "error");
      }
    })
    .catch((error) => {
      log("Error waiting for consent systems", error, "error");
      // Fallback: try to initialize anyway with safety checks
      try {
        document.addEventListener("click", handleTrackClick, {
          capture: true,
          passive: true,
        });
        trackRolexWidgetClick();
      } catch (fallbackError) {
        log("Fallback tracking initialization failed", fallbackError, "error");
      }
    });
};
